import React, { useState } from 'react';
import {
  Box, Breadcrumbs, Button, CircularProgress, Dialog, DialogActions, DialogContent, IconButton, List,
  ListItemButton, ListItemIcon, ListItemText, TextField, Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import axios from 'axios';
import type { AxiosResponse } from 'axios';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery } from 'react-query';
import { requestError } from './messages';
import type {
  ApiData, SharePointFolder, SharePointFolderListing, SharePointLibrary, SharePointSite,
} from './types';

const API = 'api/integrations/sharepoint';

interface FolderPickerDialogProps {
  open: boolean;
  workspaceId: number;
  onClose: () => void;
  onChosen: () => void;
}

interface PickerListProps {
  loading: boolean;
  error: unknown;
  empty: string;
  children: React.ReactNode;
  count: number;
}

interface CrumbButtonProps {
  label: string;
  onClick: () => void;
}

function CrumbButton({ label, onClick }: CrumbButtonProps): JSX.Element {
  return (
    <Button size='small' onClick={onClick} sx={{ textTransform: 'none', minWidth: 0, p: 0 }}>
      {label}
    </Button>
  );
}

function PickerList({
  loading, error, empty, children, count,
}: PickerListProps): JSX.Element {
  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>;
  }
  if (error !== null) {
    return <Typography color='error' sx={{ py: 2 }}>{requestError(error, 'read SharePoint')}</Typography>;
  }
  if (count === 0) {
    return <Typography color='text.secondary' sx={{ py: 2 }}>{empty}</Typography>;
  }
  return <List dense sx={{ maxHeight: 360, overflowY: 'auto' }}>{children}</List>;
}

export default function FolderPickerDialog({
  open, workspaceId, onClose, onChosen,
}: FolderPickerDialogProps): JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [site, setSite] = useState<SharePointSite | null>(null);
  const [library, setLibrary] = useState<SharePointLibrary | null>(null);
  const [trail, setTrail] = useState<SharePointFolder[]>([]);
  const currentFolder = trail.length > 0 ? trail[trail.length - 1] : null;

  const sites = useQuery(
    ['get/sharepoint-sites', workspaceId, search],
    async () => axios({ url: `${API}/sites/`, method: 'get', params: { workspace: workspaceId, search } }),
    { enabled: open && site === null, select: (res: AxiosResponse<ApiData<SharePointSite[]>>) => res.data.data },
  );
  const libraries = useQuery(
    ['get/sharepoint-libraries', workspaceId, site?.id],
    async () => axios({ url: `${API}/libraries/`, method: 'get', params: { workspace: workspaceId, site_id: site?.id } }),
    { enabled: open && site !== null && library === null, select: (res: AxiosResponse<ApiData<SharePointLibrary[]>>) => res.data.data },
  );
  const folders = useQuery(
    ['get/sharepoint-folders', workspaceId, library?.id, currentFolder?.id],
    async () => axios({
      url: `${API}/folders/`,
      method: 'get',
      params: { workspace: workspaceId, drive_id: library?.id, item_id: currentFolder?.id },
    }),
    { enabled: open && library !== null, select: (res: AxiosResponse<ApiData<SharePointFolderListing>>) => res.data.data },
  );

  const reset = (): void => {
    setSearchText('');
    setSearch('');
    setSite(null);
    setLibrary(null);
    setTrail([]);
  };
  const close = (): void => {
    reset();
    onClose();
  };

  const { mutate: choose, isLoading: choosing } = useMutation(
    async () => axios({
      url: `${API}/root-folder/`,
      method: 'post',
      params: { workspace: workspaceId },
      data: { drive_id: library?.id, item_id: currentFolder?.id ?? 'root' },
    }),
    {
      onSuccess: (): void => {
        enqueueSnackbar('Folder saved. Strada is reading it now; this can take a few minutes.');
        reset();
        onChosen();
      },
      onError: (error: unknown): void => {
        enqueueSnackbar(requestError(error, 'save the folder'), { variant: 'error' });
      },
    },
  );

  const siteList = sites.data ?? [];
  const libraryList = libraries.data ?? [];
  const folderList = folders.data?.folders ?? [];

  return (
    <Dialog open={open} onClose={(): void => { if (!choosing) { close(); } }} maxWidth='sm' fullWidth>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, pt: 2,
      }}
      >
        <Typography variant='h6' sx={{ fontWeight: 600 }}>Choose your leases folder</Typography>
        <IconButton onClick={close} disabled={choosing} size='small' aria-label='Close'><ClearIcon /></IconButton>
      </Box>
      <DialogContent>
        {site === null && (
          <>
            <Typography color='text.secondary' sx={{ mb: 2 }}>
              Find the SharePoint site that holds your leases.
            </Typography>
            <Box
              component='form'
              sx={{ display: 'flex', gap: 1 }}
              onSubmit={(e: React.FormEvent): void => { e.preventDefault(); setSearch(searchText.trim()); }}
            >
              <TextField
                size='small'
                fullWidth
                placeholder='Site name, or paste the site address'
                value={searchText}
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => { setSearchText(e.target.value); }}
              />
              <Button type='submit' variant='outlined'>Search</Button>
            </Box>
            <PickerList
              loading={sites.isLoading}
              error={sites.error}
              count={siteList.length}
              empty="No sites found. Try another name, or paste the site's address from your browser (new sites can take a while to show up in search)."
            >
              {siteList.map((s) => (
                <ListItemButton key={s.id} onClick={(): void => { setSite(s); }}>
                  <ListItemIcon><LanguageIcon /></ListItemIcon>
                  <ListItemText primary={s.name} secondary={s.web_url} />
                </ListItemButton>
              ))}
            </PickerList>
          </>
        )}

        {site !== null && (
          <Breadcrumbs sx={{ mb: 1 }}>
            <CrumbButton label='Sites' onClick={reset} />
            <CrumbButton label={site.name} onClick={(): void => { setLibrary(null); setTrail([]); }} />
            {library !== null && <CrumbButton label={library.name} onClick={(): void => { setTrail([]); }} />}
            {trail.map((folder, index) => (
              <CrumbButton key={folder.id} label={folder.name} onClick={(): void => { setTrail(trail.slice(0, index + 1)); }} />
            ))}
          </Breadcrumbs>
        )}

        {site !== null && library === null && (
          <PickerList loading={libraries.isLoading} error={libraries.error} count={libraryList.length} empty='This site has no document libraries.'>
            {libraryList.map((l) => (
              <ListItemButton key={l.id} onClick={(): void => { setLibrary(l); }}>
                <ListItemIcon><LibraryBooksOutlinedIcon /></ListItemIcon>
                <ListItemText primary={l.name} />
              </ListItemButton>
            ))}
          </PickerList>
        )}

        {library !== null && (
          <PickerList loading={folders.isLoading} error={folders.error} count={folderList.length} empty='No subfolders here.'>
            {folderList.map((folder) => (
              <ListItemButton key={folder.id} onClick={(): void => { setTrail([...trail, folder]); }}>
                <ListItemIcon><FolderOutlinedIcon /></ListItemIcon>
                <ListItemText primary={folder.name} secondary={`${folder.child_count} items`} />
              </ListItemButton>
            ))}
          </PickerList>
        )}
        {choosing && (
          <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
            Saving the folder and reading what&apos;s in it. A large folder can take a minute or two.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close} disabled={choosing}>Cancel</Button>
        <Button
          variant='contained'
          disabled={library === null || choosing}
          startIcon={choosing ? <CircularProgress size={16} color='inherit' /> : undefined}
          onClick={(): void => { choose(); }}
        >
          {choosing && 'Reading folder…'}
          {!choosing && (library === null ? 'Use this folder' : `Use "${currentFolder?.name ?? library.name}"`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
